import { useState, createRef } from 'react';
import { useDropzone } from 'react-dropzone';
import Button from '../Button/index.tsx';
import styled, { useTheme } from 'styled-components';
import Cropper from 'react-cropper';
import 'cropperjs/dist/cropper.css';

const DropzoneContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 30px;
  border: 1px dashed ${({ theme }) => theme.text.secondary};
  border-radius: 8px;
  cursor: pointer;
  width: 100%;
  margin-bottom: 10px;
  height: 200px;
  text-align: center;
`;

const Label = styled.p`
  color: ${({ theme }) => theme.text.secondary};
  font-size: 12px;
`;

const CropperContainer = styled.div`
  width: 100%;
  max-width: 900px;
  max-height: 70vh;
  margin: 0 auto;
  position: relative;
  /* Allow panning within cropper, prevent modal swipe gesture */
  touch-action: pan-x pan-y pinch-zoom;
  -ms-touch-action: pan-x pan-y pinch-zoom;

  .cropper-container {
    max-width: 900px;
    max-height: 70vh;
    /* Ensure cropper's touch handling takes precedence */
    touch-action: pan-x pan-y pinch-zoom;
    -ms-touch-action: pan-x pan-y pinch-zoom;
  }
`;

const BleedingOverlay = styled.div`
  position: absolute;
  top: ${({ top }) => top}px;
  left: ${({ left }) => left}px;
  width: ${({ width }) => width}px;
  height: ${({ height }) => height}px;
  pointer-events: none;
  z-index: 10;

  &::before {
    content: '';
    position: absolute;
    top: ${({ bleedingPercent, height }) => height * bleedingPercent}px;
    left: ${({ bleedingPercent, width }) => width * bleedingPercent}px;
    right: ${({ bleedingPercent, width }) => width * bleedingPercent}px;
    bottom: ${({ bleedingPercent, height }) => height * bleedingPercent}px;
    border: 2px dashed rgba(255, 0, 0, 0.6);
    box-sizing: border-box;
  }

  &::after {
    content: 'Safe Area';
    position: absolute;
    top: ${({ bleedingPercent, height }) => height * bleedingPercent + 5}px;
    left: ${({ bleedingPercent, width }) => width * bleedingPercent + 5}px;
    background: rgba(255, 0, 0, 0.8);
    color: white;
    padding: 2px 6px;
    font-size: 10px;
    border-radius: 3px;
    font-weight: 600;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 20px;
`;

const UploadImage = styled.img`
  width: 53px;
  padding-bottom: 10px;
`;

const CustomizeCardImageUploader = ({
  isPhysical = false,
  onComplete,
  onCancel,
}) => {
  const [loading, setLoading] = useState(false);
  const [image, setImageState] = useState(null);
  const [showCropper, setShowCropper] = useState(false);
  const [overlayPosition, setOverlayPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
    height: 0,
  });
  const cropperRef = createRef();
  const theme = useTheme();

  const digitalAspectRatio = 407 / 226;
  const physicalAspectRatio = 85.6 / 53.98;
  const aspectRatio = isPhysical ? physicalAspectRatio : digitalAspectRatio;

  const bleedingArea = isPhysical ? 0.04 : 0;

  const onDrop = (acceptedFiles) => {
    const file = acceptedFiles[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const imageData = reader.result;
        setImageState(imageData);
        setShowCropper(true);
      };
      reader.readAsDataURL(file);
    }
  };

  const { getRootProps, getInputProps } = useDropzone({
    accept: 'image/*',
    onDrop,
  });

  const getCropData = async () => {
    if (!onComplete) return;
    setLoading(true);
    try {
      if (cropperRef.current?.cropper) {
        const croppedDataUrl = cropperRef.current.cropper
          .getCroppedCanvas()
          .toDataURL();
        await onComplete(croppedDataUrl);
      }
    } finally {
      setLoading(false);
    }
  };

  const updateOverlayPosition = () => {
    if (!isPhysical || !cropperRef.current?.cropper) return;

    const cropper = cropperRef.current.cropper;
    const cropBoxData = cropper.getCropBoxData();
    setOverlayPosition({
      top: cropBoxData.top,
      left: cropBoxData.left,
      width: cropBoxData.width,
      height: cropBoxData.height,
    });
  };

  const handleReady = () => {
    if (!cropperRef.current?.cropper) return;
    updateOverlayPosition();
  };

  const handleCropMove = () => {
    updateOverlayPosition();
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      setShowCropper(false);
      setImageState(null);
    }
  };

  return (
    <div>
      <h3
        style={{
          textAlign: 'center',
          paddingBottom: 24,
        }}
      >
        Customize Card
      </h3>
      {!showCropper ? (
        <>
          <DropzoneContainer {...getRootProps()}>
            <UploadImage src='/upload.webp' />
            <input {...getInputProps()} />
            <p
              style={{
                fontSize: 14,
              }}
            >
              Drag image here or{' '}
              <span
                style={{
                  color: `${theme.primary.primary}`,
                  fontWeight: 600,
                }}
              >
                click to upload
              </span>
            </p>
          </DropzoneContainer>
          <Label>jpg/png files with a size less than 1MB.</Label>
        </>
      ) : (
        <>
          <CropperContainer>
            {isPhysical && overlayPosition.width > 0 && (
              <BleedingOverlay
                top={overlayPosition.top}
                left={overlayPosition.left}
                width={overlayPosition.width}
                height={overlayPosition.height}
                bleedingPercent={bleedingArea}
              />
            )}
            {image && (
              <Cropper
                ref={cropperRef}
                initialAspectRatio={aspectRatio}
                src={image}
                viewMode={1}
                minCropBoxHeight={10}
                minCropBoxWidth={10}
                background={false}
                responsive={true}
                autoCropArea={1}
                checkOrientation={false}
                guides={true}
                aspectRatio={aspectRatio}
                crop={handleCropMove}
                ready={handleReady}
                zoom={handleCropMove}
              />
            )}
          </CropperContainer>
          <ButtonContainer>
            <Button variant='secondary' onClick={handleCancel}>
              Cancel
            </Button>
            <Button variant='primary' onClick={getCropData} loading={loading}>
              Crop &amp; Save
            </Button>
          </ButtonContainer>
        </>
      )}
    </div>
  );
};

export default CustomizeCardImageUploader;
